import { useState, useEffect, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { fmt$, fmt$0 } from '../utils/formatters';
import InvoiceReview from './InvoiceReview';
import { restoreSwap } from '../lib/invoiceSwap';
import { suggestEC } from '../lib/feeDetection';
import { postProcessExtraction } from '../lib/invoiceApi';
import { sharedConfirmInvoice } from '../lib/confirmInvoice';

export default function DuplicateReviewPage({taskId,directData,orgId,uid,ings,setIngs,mi,batches,bc,settings,save,onConfirm,setPurchases,setOtherExp,t,si,sb,sh,sd,mn,setPg,refreshBell}){
const[staging,setStaging]=useState(null);
const[task,setTask]=useState(null);
const[existingInv,setExistingInv]=useState(null);
const[existingLines,setExistingLines]=useState([]);
const[loading,setLoading]=useState(true);
const[error,setError]=useState('');
const[choice,setChoice]=useState(null); // 'new' | 'existing'
const[confirming,setConfirming]=useState(false);
const[priceUpdates,setPriceUpdates]=useState({});
const[newItemEC,setNewItemEC]=useState({});
const[newItemCat,setNewItemCat]=useState({});
const[showDiff,setShowDiff]=useState(false);
const[reviewSwapMode,setReviewSwapMode]=useState(false);
const[restoreError,setRestoreError]=useState('');
const[restoring,setRestoring]=useState(false);
const[activeId,setActiveId]=useState(null);
const[deferring,setDeferring]=useState(false);
const[deferMsg,setDeferMsg]=useState('');

useEffect(()=>{
  if(!orgId)return;
  // Review-swap mode: comparing two committed invoices (past swap)
  if(directData?.mode==='review-swap'){
    (async()=>{
      const oldId=directData.oldId;const newId=directData.newId;
      const[{data:oldInv},{data:oldLines},{data:newInv},{data:newLines}]=await Promise.all([
        supabase.from('invoices').select('*').eq('id',oldId).maybeSingle(),
        supabase.from('invoice_line_items').select('*').eq('invoice_id',oldId).order('id'),
        supabase.from('invoices').select('*').eq('id',newId).maybeSingle(),
        supabase.from('invoice_line_items').select('*').eq('invoice_id',newId).order('id'),
      ]);
      setExistingInv(oldInv);setExistingLines(oldLines||[]);
      setStaging({vendor:newInv?.vendor,invoice_number:newInv?.invoice_number,invoice_date:newInv?.invoice_date,grand_total:Number(newInv?.grand_total||0),matched_items:newLines||[],extracted_data:{subtotal:newInv?.subtotal,tax:newInv?.tax,freight:newInv?.freight,bottle_deposit:newInv?.bottle_deposit,doc_type:newInv?.doc_type}});
      setActiveId(!newInv?.superseded_by?newId:oldId);
      setReviewSwapMode(true);setLoading(false);
    })();return;
  }
  // Direct mode: data passed from InvoiceScanner (no staging row, no task)
  if(directData){
    (async()=>{
      const existingId=directData.existingInvoiceId;
      const[{data:inv},{data:lines}]=await Promise.all([
        existingId?supabase.from('invoices').select('*').eq('id',existingId).maybeSingle():{data:null},
        existingId?supabase.from('invoice_line_items').select('*').eq('invoice_id',existingId).order('id'):{data:[]},
      ]);
      setStaging({extracted_data:directData.extracted,matched_items:directData.matched,vendor:directData.vendor,invoice_number:directData.invNumber,invoice_date:directData.extracted.invoice_date,grand_total:directData.extracted.grand_total||(directData.extracted.subtotal||0)+(directData.extracted.bottle_deposit||0)+(directData.extracted.freight||0),storage_path:null,uploadedFile:directData.uploadedFile});
      setExistingInv(inv);setExistingLines(lines||[]);
      const items=directData.matched||[];const updates={};items.filter(i=>i.price_changed).forEach(i=>{if((i.amount===0||!i.amount)&&(i.qty_shipped||0)>0&&(i.unit_price||0)>0)return;updates[i.matched_ingredient_id]=true;});setPriceUpdates(updates);
      setLoading(false);
    })();return;
  }
  // Task mode: load from staging + task
  if(!taskId){setLoading(false);setPg('purchases');return;}
  (async()=>{
    const{data:tk}=await supabase.from('tasks').select('*').eq('id',taskId).single();
    if(!tk){setError('Task not found');setLoading(false);return;}
    setTask(tk);
    const stagingId=tk.payload?.staging_id||tk.target_id;
    const existingId=tk.payload?.existing_invoice_id;
    const[{data:st},{data:inv},{data:lines}]=await Promise.all([
      supabase.from('invoice_staging').select('*').eq('id',stagingId).single(),
      existingId?supabase.from('invoices').select('*').eq('id',existingId).maybeSingle():{data:null},
      existingId?supabase.from('invoice_line_items').select('*').eq('invoice_id',existingId).order('id'):{data:[]},
    ]);
    if(!st){setError('Staging data not found');setLoading(false);return;}
    if(st.extracted_data?.line_items)st.extracted_data.line_items=postProcessExtraction(st.extracted_data.line_items);
    setStaging(st);
    setExistingInv(inv);
    setExistingLines(lines||[]);
    const items=st.matched_items||[];
    const updates={};
    items.filter(i=>i.price_changed).forEach(i=>{if((i.amount===0||!i.amount)&&(i.qty_shipped||0)>0&&(i.unit_price||0)>0)return;updates[i.matched_ingredient_id]=true;});
    setPriceUpdates(updates);
    setLoading(false);
  })();
},[taskId,orgId]);

const newMatchedItems=(staging?.matched_items||[]);
const lineDiff=useMemo(()=>{
  const items=newMatchedItems.filter(i=>i.amount&&i.amount>0);
  const keyOf=li=>(li.item_number||'').trim()||(li.description||'').toLowerCase().trim();
  const oldMap=new Map();existingLines.forEach(li=>{const k=keyOf(li);if(k)oldMap.set(k,li);});
  const newMap=new Map();items.forEach(li=>{const k=keyOf(li);if(k)newMap.set(k,li);});
  const added=[],removed=[],changed=[];
  newMap.forEach((li,k)=>{
    const old=oldMap.get(k);
    if(!old)added.push(li);
    else if(Math.abs(Number(li.amount)-Number(old.amount))>0.01)changed.push({desc:li.description,oldAmt:Number(old.amount),newAmt:Number(li.amount)});
  });
  oldMap.forEach((li,k)=>{if(!newMap.has(k)&&Number(li.amount)>0)removed.push(li);});
  return{added,removed,changed};
},[existingLines,newMatchedItems]);

async function keepExisting(){
  setConfirming(true);
  try{
    if(task){
      // Task mode: clean up staging + complete task
      await supabase.from('invoice_staging').delete().eq('id',staging.id);
      const now=new Date().toISOString();
      await supabase.from('tasks').update({status:'completed',completed_at:now,completed_by:uid}).eq('id',task.id);
      await supabase.from('task_events').insert({org_id:orgId,task_id:task.id,event_type:'status_changed',old_value:'pending',new_value:'completed',detail:{choice:'keep_existing',note:'Duplicate dismissed, kept existing invoice'},actor_id:uid});
      if(refreshBell)refreshBell();
      setPg('tasks');
    }else{
      // Direct mode: just go back to purchases
      setPg('purchases');
    }
  }catch(e){setError(e.message);setConfirming(false);}
}

async function switchToNew(reconcileInfo){
  setConfirming(true);
  try{
    const extracted=staging.extracted_data;
    const matched=staging.matched_items||[];
    const vendor=staging.vendor;
    const invNumber=staging.invoice_number;
    const invDate=staging.invoice_date||new Date().toISOString().split('T')[0];
    const grandTotal=staging.grand_total;
    const invoiceId=`inv_${Date.now()}`;

    // Upload file to permanent storage
    let fileUrl=null;
    if(staging.storage_path){
      // Task mode: move from tmp to permanent
      const ext=staging.storage_path.split('.').pop()||'jpg';
      const permPath=`${orgId}/${invoiceId}.${ext}`;
      const{data:dlData,error:dlErr}=await supabase.storage.from('invoices').download(staging.storage_path);
      if(!dlErr&&dlData){await supabase.storage.from('invoices').upload(permPath,dlData,{upsert:false});fileUrl=permPath;}
      await supabase.storage.from('invoices').remove([staging.storage_path]);
    }else if(staging.uploadedFile){
      // Direct mode: upload from base64
      const uf=staging.uploadedFile;
      const permPath=`${orgId}/${invoiceId}.${uf.ext}`;
      const fileBytes=Uint8Array.from(atob(uf.base64),c=>c.charCodeAt(0));
      const{error:upErr}=await supabase.storage.from('invoices').upload(permPath,fileBytes,{contentType:uf.type,upsert:false});
      if(!upErr)fileUrl=permPath;
    }

    // Step 1: self-supersede old row (own id satisfies FK, non-null excludes from unique index)
    if(existingInv){
      const{error:e1}=await supabase.from('invoices').update({superseded_by:existingInv.id}).eq('id',existingInv.id).select();
      if(e1)throw new Error('Failed to mark existing invoice superseded: '+e1.message);
    }else{
      throw new Error('Cannot supersede — existingInv is missing at confirm time');
    }

    // Steps 2-3: insert new invoice then fix superseded_by to real new id (rollback step 1 on failure)
    try{
      const{error:invErr}=await supabase.from('invoices').insert({
        id:invoiceId,user_id:uid,org_id:orgId,vendor,
        invoice_number:invNumber,invoice_date:invDate,
        subtotal:extracted.subtotal,tax:extracted.tax||0,freight:extracted.freight||0,
        bottle_deposit:extracted.bottle_deposit||0,grand_total:grandTotal,
        total:grandTotal,status:'confirmed',uploaded_by_name:'',file_url:fileUrl,doc_type:extracted.doc_type||'invoice',
        doc_quality:extracted.document_quality||null,doc_quality_confidence:typeof extracted.document_quality_confidence==='number'?extracted.document_quality_confidence:null,
        doc_timestamp:extracted.document_timestamp||null
      });
      if(invErr)throw invErr;
      if(existingInv){
        const{error:e3}=await supabase.from('invoices').update({superseded_by:invoiceId}).eq('id',existingInv.id);
        if(e3)throw e3;
        // Flag old invoice's purchases and expenses as superseded
        const{error:e4}=await supabase.from('purchases').update({is_superseded:true}).eq('invoice_id',existingInv.id);
        if(e4)throw e4;
        // Remove old purchase from UI state immediately
        if(setPurchases)setPurchases(p=>p.filter(x=>x.invoiceId!==existingInv.id));
        // Flag old categorized_expenses as superseded
        await supabase.from('categorized_expenses').update({is_superseded:true}).eq('source_invoice_id',existingInv.id);
        if(setOtherExp)setOtherExp(p=>p.filter(x=>x.sourceInvoiceId!==existingInv.id));
      }
    }catch(e){
      if(existingInv)await supabase.from('invoices').update({superseded_by:null}).eq('id',existingInv.id);
      throw new Error(e.message||'Failed to insert new invoice');
    }

    // Delegate remaining steps to shared function (invoice already inserted above)
    const result=await sharedConfirmInvoice({
      orgId,uid,extracted,matched,vendor,invNumber,invDate,grandTotal,
      confirmPath:'duplicate',reconcileInfo,priceUpdates,newItemEC,newItemCat,
      ings,setIngs,setOtherExp,setPurchases,
      settings,mi,batches,bc,save,
      onConfirm,refreshBell,
      skipDupCheck:true,preInsertedInvoiceId:invoiceId,
    });
    if(result?.error)throw new Error(result.error);
    // Complete task + delete staging (task mode only)
    if(task){
      const now=new Date().toISOString();
      await supabase.from('tasks').update({status:'completed',completed_at:now,completed_by:uid}).eq('id',task.id);
      await supabase.from('task_events').insert({org_id:orgId,task_id:task.id,event_type:'status_changed',old_value:'pending',new_value:'completed',detail:{choice:'switch_to_new',new_invoice_id:invoiceId,superseded_id:existingInv?.id},actor_id:uid});
      await supabase.from('invoice_staging').delete().eq('id',staging.id);
    }

    if(refreshBell)refreshBell();
    setPg(task?'tasks':'purchases');
  }catch(e){console.error('[DuplicateReview] Error:',e);setError(e.message);setConfirming(false);}
}

if(loading)return <div style={{textAlign:'center',padding:40,color:t.td}}>Loading duplicate data...</div>;
if(error&&!staging)return <div style={{textAlign:'center',padding:40}}><div style={{color:t.ac,marginBottom:12}}>{error}</div><button onClick={()=>setPg(task?'tasks':'purchases')} style={{...sb,background:t.bl}}>Back</button></div>;
if(!existingInv&&!reviewSwapMode&&staging)return <div style={{textAlign:'center',padding:40}}>
<div style={{color:t.yl,marginBottom:8,fontSize:14,fontWeight:600}}>Existing invoice not found</div>
<div style={{color:t.td,fontSize:12,marginBottom:16}}>The invoice this was compared against may have been deleted. This scan is not a duplicate — you can confirm it as a new invoice.</div>
<div style={{display:'flex',gap:8,justifyContent:'center'}}>
<button onClick={()=>setPg(task?'tasks':'purchases')} style={{...sb,background:t.sf,color:t.tm,border:`1px solid ${t.bd}`}}>Back</button>
<button onClick={async()=>{if(task){await supabase.from('tasks').update({target_page:'invoice-review'}).eq('id',task.id);}setPg(task?'tasks':'purchases');}} style={{...sb,background:t.gn}}>Confirm as new invoice</button>
</div>
</div>;

async function deferDuplicate(){
  if(!staging||!orgId)return;
  setDeferring(true);setError('');
  try{
    // Ensure file is in storage
    let storagePath=staging.storage_path||null;
    if(!storagePath&&staging.uploadedFile){
      storagePath=`${orgId}/tmp/${Date.now()}-deferred.${staging.uploadedFile.ext}`;
      const fileBytes=Uint8Array.from(atob(staging.uploadedFile.base64),c=>c.charCodeAt(0));
      await supabase.storage.from('invoices').upload(storagePath,fileBytes,{contentType:staging.uploadedFile.type,upsert:true});
    }
    // Create or reuse staging row
    const stagingId=staging.id||crypto.randomUUID();
    if(!staging.id){
      const{error:stgErr}=await supabase.from('invoice_staging').insert({
        id:stagingId,org_id:orgId,
        vendor:staging.vendor,invoice_number:staging.invoice_number,
        invoice_date:staging.invoice_date||null,grand_total:staging.grand_total||0,
        extracted_data:staging.extracted_data,matched_items:staging.matched_items||[],
        storage_path:storagePath,
      });
      if(stgErr)throw new Error(stgErr.message);
    }
    // Create task with duplicate context
    const{error:tkErr}=await supabase.from('tasks').insert({
      org_id:orgId,type:'invoice_review',status:'pending',
      title:`Duplicate: ${staging.vendor} $${(staging.grand_total||0).toFixed(2)}`,
      note:staging.invoice_number?`Invoice #${staging.invoice_number} — duplicate needs resolution`:'Duplicate invoice needs resolution',
      source:'manual',priority:5,
      target_page:'duplicate-review',target_id:stagingId,
      payload:{staging_id:stagingId,existing_invoice_id:existingInv?.id,choice:choice||null},
    });
    if(tkErr)throw new Error(tkErr.message);
    setDeferMsg('Saved to Tasks for later');
    setTimeout(()=>{setPg(task?'tasks':'purchases');},1500);
    if(refreshBell)refreshBell();
  }catch(e){
    console.error('[DuplicateReviewPage] Defer error:',e);
    setError(e.message||'Failed to save for later');
  }finally{setDeferring(false);}
}

async function viewPdf(storagePath){
  if(!storagePath)return;
  const{data,error:err}=await supabase.storage.from('invoices').createSignedUrl(storagePath,3600);
  if(err||!data?.signedUrl){alert('Could not load PDF');return;}
  window.open(data.signedUrl,'_blank');
}
function viewBase64(uploadedFile){
  if(!uploadedFile)return;
  const bytes=Uint8Array.from(atob(uploadedFile.base64),c=>c.charCodeAt(0));
  const blob=new Blob([bytes],{type:uploadedFile.type});
  const url=URL.createObjectURL(blob);
  window.open(url,'_blank');
}

const existingPdfPath=existingInv?.file_url||null;
const newPdfPath=staging?.storage_path||null;
const newUploadedFile=staging?.uploadedFile||null;

const newData=staging.extracted_data;
const newMatched=newMatchedItems;
const totalDiff=staging.grand_total-(existingInv?.grand_total||0);
const newLineCount=newMatched.filter(i=>i.amount&&i.amount>0).length;
const priceChangeCount=newMatched.filter(i=>i.price_changed&&priceUpdates[i.matched_ingredient_id]).length;


return(<div>
<div style={{display:'flex',alignItems:'center',gap:12,marginBottom:20}}>
<button onClick={()=>setPg(task?'tasks':'purchases')} style={{...sb,background:'transparent',color:t.tm,padding:'4px 0',fontSize:11}}>← Back</button>
<h1 style={{fontSize:20,fontWeight:700,margin:0}}>Duplicate Invoice</h1>
</div>

{/* Summary banner */}
<div style={{background:reviewSwapMode?'rgba(59,130,246,0.06)':'rgba(200,55,45,0.06)',border:`1px solid ${reviewSwapMode?t.bl:t.ac}`,borderRadius:10,padding:16,marginBottom:20}}>
<div style={{fontSize:14,fontWeight:700,color:reviewSwapMode?t.bl:t.ac,marginBottom:6}}>{reviewSwapMode?'Reviewing past swap':'Same invoice uploaded twice'}</div>
<div style={{fontSize:12,color:t.td}}>{staging.vendor} #{staging.invoice_number} on {staging.invoice_date} — {reviewSwapMode?'original vs current. Restore the original if this swap was a mistake.':'choose which version to keep.'}</div>
</div>

{/* What changed */}
{existingInv&&staging&&<div style={{background:t.sf,borderRadius:8,border:`1px solid ${t.bd}`,padding:'12px 16px',marginBottom:16,fontSize:11}}>
<div style={{fontSize:12,fontWeight:700,marginBottom:6}}>What changed</div>
{(()=>{const oldT=Number(existingInv.grand_total||0);const newT=Number(staging.grand_total||0);const diff=newT-oldT;return<div style={{marginBottom:lineDiff.added.length||lineDiff.removed.length||lineDiff.changed.length?6:0}}>
<span style={{color:t.tm}}>Total: </span><span style={{fontFamily:mn}}>${fmt$0(oldT)}</span><span style={{color:t.td}}> → </span><span style={{fontFamily:mn,fontWeight:600}}>${fmt$0(newT)}</span>{Math.abs(diff)>0.01&&<span style={{fontFamily:mn,fontWeight:600,color:diff>0?t.gn:t.ac,marginLeft:6}}>({diff>0?'+':'−'}${fmt$0(Math.abs(diff))})</span>}
</div>;})()}
{lineDiff.added.map(li=><div key={li.item_number||li.description} style={{color:t.gn}}>+ {li.description} <span style={{fontFamily:mn}}>${fmt$0(li.amount)}</span></div>)}
{lineDiff.removed.map(li=><div key={li.item_number||li.description} style={{color:t.ac}}>− {li.description} <span style={{fontFamily:mn}}>${fmt$0(li.amount)}</span></div>)}
{lineDiff.changed.map(c=><div key={c.desc} style={{color:t.yl}}>{c.desc}: <span style={{fontFamily:mn}}>${fmt$0(c.oldAmt)}</span> → <span style={{fontFamily:mn,fontWeight:600}}>${fmt$0(c.newAmt)}</span></div>)}
{!lineDiff.added.length&&!lineDiff.removed.length&&!lineDiff.changed.length&&<div style={{color:t.td,fontStyle:'italic'}}>No line-item differences</div>}
</div>}

{/* Side by side comparison */}
<div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:20}}>

{/* Existing invoice */}
<div style={{position:'relative',background:t.cd,borderRadius:12,border:`2px solid ${reviewSwapMode?(activeId===directData.oldId?t.gn:t.bd):(choice==='existing'?t.gn:t.bd)}`,padding:16,cursor:reviewSwapMode?(activeId===directData.oldId?'default':'pointer'):'pointer',transition:'border 0.2s'}} onClick={()=>{if(reviewSwapMode){if(activeId!==directData.oldId&&!restoring){setRestoring(true);setRestoreError('');restoreSwap({oldId:directData.oldId,newId:directData.newId}).then(()=>setActiveId(directData.oldId)).catch(e=>setRestoreError(e.message||'Switch failed')).finally(()=>setRestoring(false));}}else setChoice('existing');}}>
{reviewSwapMode&&<div style={{position:'absolute',top:8,left:'50%',transform:'translateX(-50%)',width:18,height:18,borderRadius:'50%',border:`2px solid ${activeId===directData.oldId?t.gn:t.tm}`,background:activeId===directData.oldId?t.gn:'transparent',pointerEvents:'none'}}/>}
<div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
<div style={{display:'flex',alignItems:'center',gap:8}}>
<div style={{fontSize:13,fontWeight:700}}>{reviewSwapMode?'Original':'Existing Invoice'}</div>
{reviewSwapMode&&existingInv?.doc_type&&<span style={{fontSize:8,padding:'2px 6px',borderRadius:4,background:existingInv.doc_type==='invoice'?'rgba(59,130,246,0.1)':'rgba(212,168,36,0.15)',color:existingInv.doc_type==='invoice'?t.bl:t.yl,fontWeight:600,textTransform:'uppercase',letterSpacing:0.5}}>{existingInv.doc_type.replace('_',' ')}</span>}
{existingInv?.doc_quality&&existingInv.doc_quality!=='unknown'&&<span style={{fontSize:8,padding:'2px 6px',borderRadius:4,background:existingInv.doc_quality==='final'?'rgba(34,197,94,0.1)':'rgba(212,168,36,0.15)',color:existingInv.doc_quality==='final'?t.gn:t.yl,fontWeight:600}}>{existingInv.doc_quality==='final'?'Delivery Ticket':'Portal Printout'}</span>}
{existingPdfPath&&<button onClick={e=>{e.stopPropagation();viewPdf(existingPdfPath);}} style={{background:'none',border:'none',color:t.bl,cursor:'pointer',fontSize:10,fontFamily:"'DM Sans',sans-serif",padding:0}}>View PDF ↗</button>}
</div>
{reviewSwapMode&&activeId===directData.oldId&&<span style={{fontSize:8,padding:'2px 6px',borderRadius:4,background:'rgba(34,197,94,0.1)',color:t.gn,fontWeight:600}}>ACTIVE</span>}
{!reviewSwapMode&&<div style={{width:20,height:20,borderRadius:'50%',border:`2px solid ${choice==='existing'?t.gn:t.bd}`,background:choice==='existing'?t.gn:'transparent',display:'flex',alignItems:'center',justifyContent:'center'}}>{choice==='existing'&&<span style={{color:'#fff',fontSize:10}}>✓</span>}</div>}
</div>
<div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:12}}>
{[['Total','$'+fmt$0(existingInv?.grand_total||0)],['Lines',existingLines.length],['Date',existingInv?.invoice_date||'—'],['Status',reviewSwapMode?(activeId===directData.oldId?'Active':'Superseded'):'Confirmed']].map(([l,v])=>
<div key={l} style={{background:t.sf,borderRadius:6,padding:'6px 8px'}}><div style={{fontSize:8,color:t.td,textTransform:'uppercase'}}>{l}</div><div style={{fontSize:12,fontWeight:600,marginTop:1}}>{v}</div></div>)}
</div>
{existingLines.length>0&&<div style={{maxHeight:200,overflow:'auto'}}>
<table style={{width:'100%',borderCollapse:'collapse'}}>
<tbody>{existingLines.map((li,i)=><tr key={i} style={{borderBottom:`1px solid ${t.bd}`}}>
<td style={{padding:'4px 6px',fontSize:10}}>{li.description}</td>
<td style={{padding:'4px 6px',fontSize:10,fontFamily:mn,textAlign:'right'}}>${fmt$0(li.amount||0)}</td>
</tr>)}</tbody>
</table></div>}
</div>

{/* New invoice */}
<div style={{position:'relative',background:t.cd,borderRadius:12,border:`2px solid ${reviewSwapMode?(activeId===directData.newId?t.gn:t.bd):(choice==='new'?t.gn:t.bd)}`,padding:16,cursor:reviewSwapMode?(activeId===directData.newId?'default':'pointer'):'pointer',transition:'border 0.2s'}} onClick={()=>{if(reviewSwapMode){if(activeId!==directData.newId&&!restoring){setRestoring(true);setRestoreError('');restoreSwap({oldId:directData.newId,newId:directData.oldId}).then(()=>setActiveId(directData.newId)).catch(e=>setRestoreError(e.message||'Switch failed')).finally(()=>setRestoring(false));}}else setChoice('new');}}>
{reviewSwapMode&&<div style={{position:'absolute',top:8,left:'50%',transform:'translateX(-50%)',width:18,height:18,borderRadius:'50%',border:`2px solid ${activeId===directData.newId?t.gn:t.tm}`,background:activeId===directData.newId?t.gn:'transparent',pointerEvents:'none'}}/>}
<div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
<div style={{display:'flex',alignItems:'center',gap:8}}>
<div style={{fontSize:13,fontWeight:700}}>{reviewSwapMode?'Current (Replacement)':'New Upload'}</div>
{reviewSwapMode&&staging?.extracted_data?.doc_type&&<span style={{fontSize:8,padding:'2px 6px',borderRadius:4,background:staging.extracted_data.doc_type==='invoice'?'rgba(59,130,246,0.1)':'rgba(212,168,36,0.15)',color:staging.extracted_data.doc_type==='invoice'?t.bl:t.yl,fontWeight:600,textTransform:'uppercase',letterSpacing:0.5}}>{staging.extracted_data.doc_type.replace('_',' ')}</span>}
{(()=>{const nq=staging?.extracted_data?.document_quality||newData?.document_quality;return nq&&nq!=='unknown'?<span style={{fontSize:8,padding:'2px 6px',borderRadius:4,background:nq==='final'?'rgba(34,197,94,0.1)':'rgba(212,168,36,0.15)',color:nq==='final'?t.gn:t.yl,fontWeight:600}}>{nq==='final'?'Delivery Ticket':'Portal Printout'}</span>:null;})()}
{newPdfPath&&<button onClick={e=>{e.stopPropagation();viewPdf(newPdfPath);}} style={{background:'none',border:'none',color:t.bl,cursor:'pointer',fontSize:10,fontFamily:"'DM Sans',sans-serif",padding:0}}>View PDF ↗</button>}
{!newPdfPath&&newUploadedFile&&<button onClick={e=>{e.stopPropagation();viewBase64(newUploadedFile);}} style={{background:'none',border:'none',color:t.bl,cursor:'pointer',fontSize:10,fontFamily:"'DM Sans',sans-serif",padding:0}}>View PDF ↗</button>}
</div>
{reviewSwapMode&&activeId===directData.newId&&<span style={{fontSize:8,padding:'2px 6px',borderRadius:4,background:'rgba(34,197,94,0.1)',color:t.gn,fontWeight:600}}>ACTIVE</span>}
{!reviewSwapMode&&<div style={{width:20,height:20,borderRadius:'50%',border:`2px solid ${choice==='new'?t.gn:t.bd}`,background:choice==='new'?t.gn:'transparent',display:'flex',alignItems:'center',justifyContent:'center'}}>{choice==='new'&&<span style={{color:'#fff',fontSize:10}}>✓</span>}</div>}
</div>
<div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:12}}>
{[['Total','$'+fmt$0(staging.grand_total)],['Lines',newLineCount],['Date',staging.invoice_date||'—'],['Status',reviewSwapMode?(activeId===directData.newId?'Active':'Superseded'):'Pending review']].map(([l,v])=>
<div key={l} style={{background:t.sf,borderRadius:6,padding:'6px 8px'}}><div style={{fontSize:8,color:t.td,textTransform:'uppercase'}}>{l}</div><div style={{fontSize:12,fontWeight:600,marginTop:1}}>{v}</div></div>)}
</div>
{newMatched.length>0&&<div style={{maxHeight:200,overflow:'auto'}}>
<table style={{width:'100%',borderCollapse:'collapse'}}>
<tbody>{newMatched.filter(i=>i.amount&&i.amount>0).map((li,i)=><tr key={i} style={{borderBottom:`1px solid ${t.bd}`}}>
<td style={{padding:'4px 6px',fontSize:10}}>{li.description}</td>
<td style={{padding:'4px 6px',fontSize:10,fontFamily:mn,textAlign:'right'}}>${fmt$0(li.amount||0)}</td>
</tr>)}</tbody>
</table></div>}
</div>
</div>

{/* Back button (review-swap mode) */}
{reviewSwapMode&&<div style={{display:'flex',gap:8,justifyContent:'center',marginBottom:16}}>
{restoring&&<span style={{fontSize:11,color:t.tm,alignSelf:'center'}}>Switching...</span>}
<button onClick={()=>setPg('swappedinvoices')} style={{...sb,background:t.sf,color:t.tm,border:`1px solid ${t.bd}`}}>Done</button>
</div>}
{restoreError&&<div style={{fontSize:11,color:t.ac,marginBottom:10}}>{restoreError}</div>}

{/* Diff summary + confirm (live swap mode only) */}
{!reviewSwapMode&&choice&&!showDiff&&<div style={{display:'flex',justifyContent:'flex-end',gap:8}}>
<button onClick={()=>setPg(task?'tasks':'purchases')} style={{...sb,background:t.sf,color:t.tm,border:`1px solid ${t.bd}`}}>Cancel — discard scan</button>
{!task&&<button onClick={deferDuplicate} disabled={deferring} style={{...sb,background:'transparent',color:t.bl,border:`1px solid ${t.bl}`,opacity:deferring?0.5:1}}>{deferring?'Saving...':'Save for Later'}</button>}
<button onClick={()=>setShowDiff(true)} style={{...sb,background:t.bl}}>Continue with {choice==='new'?'new upload':'existing invoice'}</button>
</div>}
{!reviewSwapMode&&!choice&&<div style={{display:'flex',justifyContent:'flex-end',gap:8,marginTop:12}}>
<button onClick={()=>setPg(task?'tasks':'purchases')} style={{...sb,background:t.sf,color:t.tm,border:`1px solid ${t.bd}`,fontSize:11}}>Cancel — discard scan</button>
{!task&&<button onClick={deferDuplicate} disabled={deferring} style={{...sb,background:t.bl,fontSize:11,opacity:deferring?0.5:1}}>{deferring?'Saving...':'Save for Later'}</button>}
</div>}

{showDiff&&choice==='existing'&&<div style={{background:t.cd,borderRadius:12,border:`1px solid ${t.bd}`,padding:16,marginBottom:16}}>
<div style={{fontSize:13,fontWeight:700,marginBottom:8}}>Keep existing invoice</div>
<div style={{fontSize:12,color:t.td,marginBottom:12}}>The new upload will be discarded. No changes to master data.</div>
<div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
<button onClick={()=>{setShowDiff(false);setChoice(null);}} style={{...sb,background:t.sf,color:t.tm}}>Cancel</button>
<button onClick={keepExisting} disabled={confirming} style={{...sb,background:t.gn,opacity:confirming?0.5:1}}>{confirming?'Processing...':'Confirm — Keep Existing'}</button>
</div>
</div>}

{showDiff&&choice==='new'&&<div style={{background:t.cd,borderRadius:12,border:`1px solid ${t.bd}`,padding:16,marginBottom:16}}>
<div style={{fontSize:13,fontWeight:700,marginBottom:8}}>Switch to new upload</div>
<div style={{fontSize:12,color:t.td,marginBottom:4}}>This will:</div>
<ul style={{fontSize:12,color:t.td,margin:'0 0 12px',paddingLeft:20}}>
<li>{totalDiff>0?'Increase':'Reduce'} invoice total by ${fmt$0(Math.abs(totalDiff))}</li>
<li>Replace {existingLines.length} line items with {newLineCount}</li>
{priceChangeCount>0&&<li>Update {priceChangeCount} master price{priceChangeCount>1?'s':''}</li>}
<li>Archive the existing invoice (not deleted)</li>
</ul>
<div style={{fontSize:11,color:t.tm,marginBottom:12}}>Review the new invoice details below before confirming.</div>

{/* Full review of new invoice */}
<InvoiceReview extracted={newData} matched={newMatched} priceUpdates={priceUpdates} setPriceUpdates={setPriceUpdates} newItemEC={newItemEC} setNewItemEC={setNewItemEC} newItemCat={newItemCat} setNewItemCat={setNewItemCat} suggestEC={suggestEC} onConfirm={switchToNew} onCancel={()=>{setShowDiff(false);setChoice(null);}} t={t} si={si} sb={sb} sh={sh} sd={sd} mn={mn} error={error} ings={ings} setIngs={setIngs} settings={settings} save={save} orgId={orgId} uid={uid}/>
</div>}

{deferMsg&&<div style={{fontSize:11,color:t.gn,marginTop:10,fontWeight:600}}>{deferMsg}</div>}
{error&&<div style={{fontSize:11,color:t.ac,marginTop:10}}>{error}</div>}
</div>);
}
